<template>
  <RfDialog
    ref="cloneGuideModal"
    title="Clone Guide"
    width="580"
    confirmText="Clone"
    :disabled="
      (!cloneAppTarget?.value && 'App is required') ||
      (showEngagementCategoryField && !cloneEngagementCategory && 'Engagement category is required')
    "
    @confirm="handleClone"
    @close="emit('update:item', null)"
  >
    <div class="flex flex-col gap-4">
      <div>
        This will create a copy of your guide and its paths with the current design and details.
        <template v-if="showAppPicker"
          >If cloning to a different Engage app, Custom Goals, Triggers, Actions, and Experiments
          will not be cloned.</template
        >
      </div>
      <RfSelect
        v-if="isSuperAdmin"
        v-model="cloneCompanyTarget"
        :items="companies"
        :loading="companiesLoading"
        search="Search company..."
        itemValue="title"
        variant="outlined"
        returnObject
        sameWidth
        title="Company"
      />
      <template v-if="showAppPicker">
        <RfSelect
          v-model="cloneAppTarget"
          :items="apps"
          search="Search app..."
          itemValue="title"
          variant="outlined"
          returnObject
          sameWidth
          title="App"
        />
        <RfAlert v-if="!cloneIntoCurrApp" type="error">
          Custom Goals, Actions, Triggers, Experiments, Segments, and certain other prompt
          properties will not be cloned.
        </RfAlert>
      </template>
      <div v-if="showEngagementCategoryField" class="flex flex-col gap-2">
        <div class="text-sm text-gray-600">
          {{ ENGAGEMENT_CATEGORY_DESCRIPTION.guide }}
        </div>
        <RfSelect
          v-model="cloneEngagementCategory"
          title="Engagement Category"
          placeholder="Select an engagement category"
          :items="engagementCategories"
          variant="outlined"
          :itemDisabled="item => item.disabled"
          :itemDisabledMessage="item => item.disabledMessage"
          sameWidth
          itemValue="label"
        >
          <template #selected="{ props: tag }">
            <RfColorTag
              :title="tag.label"
              pill
              disabled
              :backgroundColor="getCategoryColor(tag.value)"
            />
          </template>
          <template #item="{ props: tag }">
            <div class="flex flex-col items-start">
              <RfColorTag
                :title="tag.label"
                pill
                disabled
                :backgroundColor="getCategoryColor(tag.value)"
              />
              <span class="text-grey-2 ml-2 text-sm">{{ tag.description }}</span>
            </div>
          </template>
        </RfSelect>
      </div>
    </div>
  </RfDialog>
</template>

<script setup>
import ApiApps from "@/apis/ApiApps";
import ApiCompanies from "@/apis/ApiCompanies";
import RfSelect from "@/components/inputs/RfSelect.vue";
import RfDialog from "@/components/modals/RfDialog.vue";
import RfAlert from "@/components/alerts/RfAlert.vue";
import { catchable } from "@/pinia/piniaUtils";
import {
  ENGAGEMENT_CATEGORY_DESCRIPTION,
  getCategoryColor,
} from "@/utils/constants/EngagementCategoryConstants";
import { useEngagementCategories } from "@/utils/composables/useEngagementCategories";
import { computed, ref, watch } from "vue";
import RfColorTag from "@/components/tags/RfColorTag.vue";

const emit = defineEmits(["clone", "update:item"]);
const props = defineProps({
  sequence: { type: Object, default: null },
  isSuperAdmin: { type: Boolean, default: false },
  isCompanyAdmin: { type: Boolean, default: false },
  currApp: { type: Object, default: () => ({}) },
  currUser: { type: Object, default: () => ({}) },
});

const apps = ref([]);
const companies = ref([]);
const companiesLoading = ref(false);
const cloneGuideModal = ref();
const cloneAppTarget = ref({});
const cloneCompanyTarget = ref({});
const cloneEngagementCategory = ref(null);

const cloneIntoCurrApp = computed(() => props.currApp.id === cloneAppTarget.value.value);
const showAppPicker = computed(
  () =>
    apps.value.length >
    (!cloneCompanyTarget.value?.value ||
    cloneCompanyTarget.value?.value === props.currApp.company_id
      ? 1
      : 0),
);
const {
  engagementCategoriesEnabled: showEngagementCategoryField,
  engagementCategoriesWithRestrictions: engagementCategories,
} = useEngagementCategories();

let lastFetchedCompanyId;
const getApps = companyId => {
  lastFetchedCompanyId = companyId;
  return catchable({
    t: async () => {
      const appsData = await ApiApps.getApps(companyId);
      apps.value = (
        props.isSuperAdmin || props.isCompanyAdmin
          ? appsData
          : appsData.filter(app => {
              const userApp = props.currUser.apps.find(userApp => userApp.id === app.id);
              return userApp && userApp.role !== "read_only";
            })
      ).map(el => ({ title: el.name, value: el.id }));
      if (showAppPicker.value)
        cloneAppTarget.value =
          apps.value.find(el => el.value === props.currApp?.id) || apps.value[0];
      else if (
        (!props.isSuperAdmin && !props.isCompanyAdmin) ||
        (apps.value.length === 1 && apps.value[0].value === props.currApp.id)
      )
        cloneAppTarget.value = { value: props.currApp.id, title: props.currApp.name };
      else cloneAppTarget.value = {};
    },
    loadable: true,
  });
};
const show = async () => {
  cloneGuideModal.value?.open();
  cloneEngagementCategory.value = props.sequence?.engagement_category || null;
  if (props.isSuperAdmin) companiesLoading.value = true;
  await Promise.all([
    ...(props.isSuperAdmin
      ? [
          catchable({
            t: async () =>
              (companies.value = (await ApiCompanies.getCompanies()).map(el => ({
                title: el.name,
                value: el.id,
              }))),
            f: () => (companiesLoading.value = false),
            loadable: true,
          }),
        ]
      : []),
    getApps(props.currApp.company_id),
  ]);
  if (props.isSuperAdmin)
    cloneCompanyTarget.value = companies.value.find(el => el.value === props.currApp.company_id);
};

const close = () => cloneGuideModal.value?.close();

const handleClone = () => {
  emit("clone", {
    targetAppId: cloneAppTarget.value?.value,
    targetAppName: cloneAppTarget.value?.title,
    sequenceId: props.sequence?.id,
    defaultCompany:
      !cloneCompanyTarget.value?.value ||
      props.currUser.company_id === cloneCompanyTarget.value?.value,
    engagementCategory: cloneEngagementCategory.value,
  });
  close();
};

watch(
  () => cloneCompanyTarget.value?.value,
  companyId => {
    if (companyId !== lastFetchedCompanyId) getApps(companyId);
  },
);

defineExpose({ show, close });
</script>
